import { FallbackProps } from 'react-error-boundary'
import { Link } from 'react-router-dom'
import { HERO_ART } from '../../../config/heroArt'

// Branded 404 shown for unknown URLs (dead links, old paths, bad guesses like
// /learning or /articles). SEO-first site: crawlers and readers get a helpful,
// navigable page — not a generic error screen. Rendered by the root
// ErrorBoundary for any 404 (see root.tsx), inside the normal header/footer.
export function NotFound() {
  const links = [
    { to: '/', label: 'Home' },
    { to: '/learnings', label: 'All writing' },
    { to: '/reviews', label: 'Book reviews' },
    { to: '/courses', label: 'Courses' },
    { to: '/mentorship', label: 'Mentorship' },
    { to: '/about', label: 'About' },
  ]
  return (
    <section className="mx-auto max-w-2xl px-4 py-20 sm:py-28 text-center">
      {/* React 19 hoists these to <head>; a 404 must not be indexed. */}
      <title>Page not found | Gazar</title>
      <meta name="robots" content="noindex, follow" />

      {/* Decorative: the "404" and the copy already say it. */}
      <img
        src={HERO_ART.notfound[0]}
        alt=""
        aria-hidden="true"
        className="mx-auto mb-8 w-full max-w-sm rounded-xl"
        loading="eager"
      />

      <p className="font-heading text-overline uppercase tracking-[0.2em] text-brand-navy/45 mb-4">Error 404</p>
      <h1 className="font-sans text-[64px] sm:text-[88px] font-bold leading-none tracking-[-0.03em] text-brand-navy">404</h1>
      <h2 className="mt-4 font-sans text-[28px] sm:text-[34px] font-bold tracking-[-0.02em] text-brand-navy">This page doesn&rsquo;t exist</h2>
      <p className="mt-4 font-body text-body-lg leading-relaxed text-brand-navy/70">
        The page you&rsquo;re looking for may have moved or never existed. Try one of these instead:
      </p>

      <nav className="mt-8 flex flex-wrap justify-center gap-3" aria-label="Helpful links">
        {links.map((l) => (
          <Link
            key={l.to}
            to={l.to}
            className="inline-flex items-center rounded-lg border border-brand-navy/20 bg-white px-4 py-2 font-heading text-body-sm font-semibold text-brand-navy transition-colors hover:border-brand-navy hover:bg-brand-navy hover:text-white"
          >
            {l.label}
          </Link>
        ))}
      </nav>

      <p className="mt-8 font-body text-body-sm text-brand-navy/55">
        Looking for an article?{' '}
        <Link to="/search" className="font-semibold text-brand-navy underline decoration-brand-navy/40 hover:decoration-brand-navy/80">
          Search all writing
        </Link>
        .
      </p>
    </section>
  )
}

export function PageErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const message = error instanceof Error ? error.message : String(error)
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-brand-cream px-4">
      <h1 className="font-heading text-h2 font-bold text-brand-navy mb-3">Something went wrong</h1>
      <p className="font-body text-body text-ink-soft mb-6 text-center max-w-md">
        An unexpected error occurred. Try refreshing the page or go back to the home page.
      </p>
      {process.env.NODE_ENV === 'development' && (
        <pre className="mb-6 max-w-xl w-full overflow-auto rounded border border-border-subtle bg-white p-4 font-mono text-xs text-brand-red">
          {message}
        </pre>
      )}
      <div className="flex gap-4">
        <button
          onClick={resetErrorBoundary}
          className="font-heading text-cta font-bold text-white bg-brand-navy px-5 py-3 rounded border border-brand-navy hover:opacity-90 transition-opacity duration-150"
        >
          Try again
        </button>
        <Link
          to="/"
          className="font-heading text-cta font-bold text-brand-navy px-5 py-3 rounded border border-brand-navy hover:opacity-90 transition-opacity duration-150"
        >
          Home
        </Link>
      </div>
    </div>
  )
}

export function SectionErrorFallback({ resetErrorBoundary }: FallbackProps) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 border border-border-subtle rounded bg-brand-cream">
      <p className="font-body text-body text-ink-soft mb-4 text-center">
        This section failed to render.
      </p>
      <button
        onClick={resetErrorBoundary}
        className="font-heading text-body-sm font-bold text-brand-navy underline hover:opacity-80 transition-opacity duration-150"
      >
        Try again
      </button>
    </div>
  )
}
